/**
 * Функциональные зоны генплана Калининграда, редакция 2024 года.
 *
 * Таксономия и цвета сняты с легенды самого листа («Условные обозначения»),
 * а не придуманы: лист растровый, никаких атрибутов у него нет, и единственный
 * способ сказать, что за зона под точкой, — сопоставить цвет пикселя с
 * образцом из легенды. Поэтому цвета здесь обязаны совпадать с чертежом, а не
 * быть «примерно похожими».
 *
 * Важно: зоны названы словами, а не кодами вида Ж-1 или ОД-2. В генплане
 * Калининграда кодов зон нет вовсе — они появляются только в правилах
 * землепользования и застройки, а это другой документ. Палитра по кодам из
 * zones.ts относится к векторным слоям и к этому листу отношения не имеет.
 *
 * Образцы сняты с легенды листа ФГИС ТП редакции 07.2024 — того самого, что
 * опубликован. Легенда геопортальной редакции проверена отдельно: все двадцать
 * одна позиция совпадает, наибольшее расхождение 8,1 единицы при допуске
 * сопоставления 28. То есть таблица годится для обеих редакций, но записаны
 * значения того листа, по которому карта отвечает.
 *
 * Образцы взяты из колонки «сущ.» (существующие зоны). У планируемых зон на
 * чертеже тот же цвет, поверх которого положена штриховка, поэтому отдельных
 * образцов для них здесь нет: по цвету планируемую зону от существующей не
 * отличить, и обещать этого в интерфейсе нельзя.
 */

export interface GenplanZone {
  /** Цвет образца из легенды: [r, g, b]. */
  rgb: [number, number, number];
  title: string;
  description: string;
}

export const GENPLAN_ZONES_2024: GenplanZone[] = [
  {
    rgb: [255, 225, 49],
    title: "Зона застройки индивидуальными жилыми домами",
    description:
      "Индивидуальные жилые дома, дома блокированной застройки и сопутствующие объекты "
      + "первичной ступени культурно-бытового, коммунального и социального обслуживания.",
  },
  {
    rgb: [253, 171, 1],
    title: "Зона застройки малоэтажными жилыми домами",
    description:
      "До 4 этажей, включая мансардный. Малоэтажные и индивидуальные жилые дома, дома "
      + "блокированной застройки и сопутствующие объекты обслуживания.",
  },
  {
    rgb: [255, 85, 1],
    title: "Зона застройки среднеэтажными жилыми домами",
    description:
      "От 5 до 8 этажей, включая мансардный. Многоквартирные жилые дома и сопутствующие "
      + "объекты обслуживания, инженерной и транспортной инфраструктуры.",
  },
  {
    rgb: [255, 52, 48],
    title: "Зона застройки многоэтажными жилыми домами",
    description:
      "9 этажей и более. Многоквартирные жилые дома и сопутствующие объекты обслуживания, "
      + "инженерной и транспортной инфраструктуры.",
  },
  {
    rgb: [196, 0, 124],
    title: "Зона смешанной и общественно-деловой застройки",
    description:
      "Общественно-деловые и жилые здания вместе с сопутствующими объектами первичной "
      + "ступени культурно-бытового, коммунального и социального обслуживания.",
  },
  {
    rgb: [255, 2, 197],
    title: "Общественно-деловые зоны",
    description:
      "Офисы, административные здания, банки, страховые компании, гостиницы, культовые "
      + "здания, научно-исследовательские учреждения, торговые центры, магазины.",
  },
  {
    rgb: [164, 39, 165],
    title: "Многофункциональная общественно-деловая зона",
    description:
      "Объекты делового, общественного, коммерческого и коммунально-бытового назначения, "
      + "а также объекты, необходимые для производственной и предпринимательской деятельности.",
  },
  {
    rgb: [201, 122, 247],
    title: "Зона специализированной общественной застройки",
    description:
      "Объекты социального назначения: образование, здравоохранение, культура и искусство, "
      + "физическая культура и массовый спорт, культовые здания, коллективные средства размещения.",
  },
  {
    rgb: [137, 92, 69],
    title: "Производственная зона",
    description:
      "Производственные предприятия, сопутствующая инженерная и транспортная инфраструктура, "
      + "а также коммерческие объекты, допускаемые к размещению в промышленных зонах.",
  },
  {
    rgb: [189, 151, 132],
    title: "Коммунально-складская зона",
    description:
      "Коммунальные предприятия, сооружения для хранения транспорта, склады, сопутствующая "
      + "инфраструктура и объекты общественно-делового назначения.",
  },
  {
    rgb: [2, 106, 145],
    title: "Зона транспортной инфраструктуры",
    description:
      "Объекты автомобильного, железнодорожного, воздушного, водного и трубопроводного "
      + "транспорта, улично-дорожная сеть и сопутствующие объекты.",
  },
  {
    rgb: [99, 100, 130],
    title: "Зона инженерной инфраструктуры",
    description:
      "Объекты водоснабжения, водоотведения, теплоснабжения, газоснабжения, "
      + "электроснабжения и связи, в том числе коридоры пропуска коммуникаций.",
  },
  {
    rgb: [170, 255, 4],
    title: "Зона садоводства, огородничества",
    description:
      "Садоводческие и огороднические некоммерческие товарищества, личные подсобные "
      + "хозяйства, огородничества и сопутствующая инфраструктура.",
  },
  {
    rgb: [210, 223, 177],
    title: "Зона сельскохозяйственных угодий",
    description: "Сельскохозяйственные угодья.",
  },
  {
    rgb: [0, 255, 200],
    title: "Зона озеленённых территорий общего пользования",
    description:
      "Парки, сады, скверы, бульвары, набережные, городские леса и зелёные насаждения "
      + "для благоустройства территории, плоскостные спортивные сооружения.",
  },
  {
    rgb: [210, 208, 255],
    title: "Зона режимных территорий",
    description: "Объекты, в отношении которых устанавливается особый режим.",
  },
  {
    rgb: [248, 182, 184],
    title: "Иные рекреационные зоны",
    description:
      "Ботанические сады, зоопарки, лугопарки, гидропарки, тематические парки иных видов.",
  },
  {
    rgb: [170, 207, 104],
    title: "Зоны специального назначения",
    description:
      "Кладбища, крематории и места захоронения, соответствующие культовые сооружения, "
      + "места складирования и захоронения отходов.",
  },
  {
    rgb: [105, 179, 102],
    title: "Зона озеленённых территорий специального назначения",
    description: "Зелёные насаждения защитного назначения, питомники декоративного озеленения.",
  },
  {
    rgb: [140, 237, 184],
    title: "Иные зоны",
    description:
      "Территории, подверженные антропогенному воздействию, нарушенные территории, "
      + "неудобья, а также сохраняемые природные ландшафты вне прочих функциональных зон.",
  },
  {
    rgb: [211, 248, 255],
    title: "Поверхностные водные объекты",
    description:
      "Море или его часть, водоёмы (озеро, пруд, водохранилище), водотоки (река, ручей, канал).",
  },
];

/**
 * Насколько цвет пикселя может отличаться от образца и всё ещё считаться им.
 *
 * Тайлы сжаты в WEBP с потерями, поэтому побайтового совпадения не бывает.
 * Запас взят заметно меньше расстояния между двумя ближайшими образцами
 * легенды (минимальное — 40 единиц), иначе соседние зоны начали бы подменять
 * друг друга, а ошибиться названием зоны хуже, чем ответить «не определено».
 */
const MAX_DISTANCE = 28;

/**
 * Сколько пикселей окна обязаны проголосовать за победителя.
 *
 * Окно 15x15 — это 225 пикселей, так что восемь голосов это около трёх
 * процентов. Порог низкий намеренно: на штриховке планируемых зон подавляющее
 * большинство пикселей вообще не попадает ни в один образец, и требовать от
 * победителя большинства означало бы не отвечать там почти никогда.
 */
const MIN_VOTES = 8;

/**
 * Во сколько раз победитель обязан опережать второго.
 *
 * Именно это, а не абсолютная доля, отделяет заливку от границы двух зон. На
 * границе оба цвета набирают сопоставимо, и тогда честный ответ — промолчать.
 */
const MIN_MARGIN = 3;

/** Ближайшая зона легенды к цвету, либо null, если ни одна не подходит. */
export function zoneByColour(
  rgb: readonly [number, number, number],
  zones: readonly GenplanZone[] = GENPLAN_ZONES_2024,
): GenplanZone | null {
  let best: GenplanZone | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const zone of zones) {
    const distance = Math.hypot(
      rgb[0] - zone.rgb[0],
      rgb[1] - zone.rgb[1],
      rgb[2] - zone.rgb[2],
    );
    if (distance < bestDistance) {
      bestDistance = distance;
      best = zone;
    }
  }

  return bestDistance <= MAX_DISTANCE ? best : null;
}

export interface ZoneVote {
  zone: GenplanZone;
  /** Доля пикселей окна, отданных победителю. */
  share: number;
}

/**
 * Зона по окну пикселей: голосование образцов легенды.
 *
 * Не «самый частый цвет», и это принципиально. Планируемые зоны нарисованы на
 * чертеже штриховкой, а её линии вместе со сглаживанием дают десятки оттенков,
 * каждый из которых встречается по несколько раз. Самым частым в таком окне
 * оказывается случайный промежуточный цвет, и заливка теряется: на пробе из
 * шестнадцати точек по городу этот способ узнал зону ровно в одной.
 *
 * Голосование устроено наоборот: каждый пиксель ищет ближайший образец
 * легенды, и если он достаточно близко — отдаёт ему голос. Пиксели штриховки,
 * подписей и дорог далеки от любого образца и просто не голосуют, вместо того
 * чтобы разваливать статистику. На той же пробе так узнаются двенадцать точек
 * из шестнадцати, а четыре оставшиеся — это действительно границы зон и
 * подписи, где промолчать правильно.
 *
 * `data` — обычный RGBA-массив из getImageData.
 */
export function zoneByVotes(
  data: Uint8ClampedArray,
  zones: readonly GenplanZone[] = GENPLAN_ZONES_2024,
): ZoneVote | null {
  const votes = new Array<number>(zones.length).fill(0);
  let pixels = 0;

  for (let i = 0; i < data.length; i += 4) {
    // Прозрачное — это вырез за границей округа, а не цвет зоны.
    if ((data[i + 3] ?? 0) < 200) continue;
    pixels += 1;

    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;

    let nearest = -1;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (let z = 0; z < zones.length; z += 1) {
      const zone = zones[z] as GenplanZone;
      const distance = Math.hypot(r - zone.rgb[0], g - zone.rgb[1], b - zone.rgb[2]);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = z;
      }
    }
    if (nearest >= 0 && nearestDistance <= MAX_DISTANCE) {
      votes[nearest] = (votes[nearest] ?? 0) + 1;
    }
  }

  if (pixels === 0) return null;

  let first = -1;
  let firstVotes = 0;
  let secondVotes = 0;
  for (let z = 0; z < votes.length; z += 1) {
    const count = votes[z] ?? 0;
    if (count > firstVotes) {
      secondVotes = firstVotes;
      firstVotes = count;
      first = z;
    } else if (count > secondVotes) {
      secondVotes = count;
    }
  }

  if (first < 0 || firstVotes < MIN_VOTES) return null;
  if (firstVotes < secondVotes * MIN_MARGIN) return null;

  const zone = zones[first];
  return zone ? { zone, share: firstVotes / pixels } : null;
}

